import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useRepository } from '@/data/repository-context'
import type { User } from '@/data/types'

const SESSION_KEY = 'pass.session'

export type AuthStatus = 'checking' | 'authenticated' | 'anonymous'

export interface AuthContextValue {
  readonly status: AuthStatus
  readonly user: User | null
  login(credentials: { readonly email: string; readonly password: string }): Promise<void>
  logout(): void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const readSession = (): boolean => {
  try {
    return window.localStorage.getItem(SESSION_KEY) === 'active'
  } catch {
    return false
  }
}

const writeSession = (active: boolean): void => {
  try {
    if (active) window.localStorage.setItem(SESSION_KEY, 'active')
    else window.localStorage.removeItem(SESSION_KEY)
  } catch {
    /* private mode — the session simply does not persist */
  }
}

/**
 * Session state for the app. There is no auth backend yet, so a successful
 * login is a repository read plus a persisted flag — but every screen already
 * talks to this contract, so wiring a real token exchange is a change here only.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const repository = useRepository()
  // Trust the persisted flag synchronously so a signed-in person never sees a
  // loading flash on navigation; the profile is then filled in behind it.
  const [status, setStatus] = useState<AuthStatus>(() =>
    readSession() ? 'authenticated' : 'anonymous',
  )
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    if (status !== 'authenticated' || user) return
    let active = true
    repository
      .getUser()
      .then((next) => {
        if (active) setUser(next)
      })
      .catch(() => {
        if (!active) return
        writeSession(false)
        setUser(null)
        setStatus('anonymous')
      })
    return () => {
      active = false
    }
  }, [repository, status, user])

  const login = useCallback(
    async ({ email }: { readonly email: string; readonly password: string }) => {
      const next = await repository.getUser()
      writeSession(true)
      setUser({ ...next, email: email || next.email })
      setStatus('authenticated')
    },
    [repository],
  )

  const logout = useCallback(() => {
    writeSession(false)
    setUser(null)
    setStatus('anonymous')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout }),
    [status, user, login, logout],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
